import React, { Fragment, useEffect, useState } from 'react'
import type { ReactNode } from 'react'
import { useBoot } from './boot-provider'
import { errorReferenceId } from '../../_utils/airbrake'
import { NoDomainsError } from '../access/no-domains-error'
import { AuthorizationRefusedError } from './authorization-error'
import { markReadyRender } from './boot-marks'
import { BootBrandedLoading } from './boot-branded-loading'
import { BootFailed, BootNoDomains, BootRestoring } from './boot-screens'

export type BootGateProps = {
    /** Rendered when there is no valid session. Nothing is rendered when omitted. */
    unauthenticated?: ReactNode
    /** Rendered over the mounted app when the session expired. */
    expired?: ReactNode
    children?: ReactNode
}

/** How long the loading screen stays up as a fading overlay once boot is ready. It outlasts the CSS transition. */
export const BOOT_FADE_OUT_MS = 850

/**
 * True for BOOT_FADE_OUT_MS after loading ends in ready. State, not a DOM side effect, so the loading
 * screen is the same mounted element throughout and its fade is a plain CSS transition.
 */
const useFadeAfterLoading = (loading: boolean, ready: boolean): boolean => {
    const [wasLoading, setWasLoading] = useState(loading)
    const [fading, setFading] = useState(false)

    // Adjusted during render so the overlay is in the same commit that mounts the app: no frame without it.
    if (loading !== wasLoading) {
        setWasLoading(loading)
        setFading(wasLoading && ready)
    }

    useEffect(() => {
        if (!fading) {
            return undefined
        }
        const timer = setTimeout(() => setFading(false), BOOT_FADE_OUT_MS)
        return () => clearTimeout(timer)
    }, [fading])

    return fading
}

/**
 * Renders one screen per boot phase. The app mounts once boot is ready, and stays mounted while a
 * session is restored. The restore status region is rendered in every phase that mounts the app, so
 * it exists before a restore starts and the restore text is announced when it appears.
 */
export const BootGate = ({ unauthenticated, expired, children }: BootGateProps) => {
    const { phase, send, restoring } = useBoot()
    const isReady = phase.phase === 'ready'
    const isLoading =
        !restoring && (phase.phase === 'identity' || phase.phase === 'session' || phase.phase === 'access')
    const fading = useFadeAfterLoading(isLoading, isReady)

    useEffect(() => {
        if (isReady) {
            markReadyRender()
        }
    }, [isReady])

    // The phases that mount the app share one child shape: the app, the loading overlay, the expired
    // element and the restore status region, each under its own key. Identity follows the key, not the
    // position, so moving between phases never remounts the overlay mid-fade or the status region
    // mid-announcement.
    switch (phase.phase) {
        case 'identity':
        case 'session':
        case 'access':
            // A restore keeps the app mounted so unsaved in-page state survives; only a failure replaces it.
            return restoring ? (
                <>
                    <Fragment key="app">{children}</Fragment>
                    <BootRestoring key="status" active />
                </>
            ) : (
                <>
                    <BootBrandedLoading key="overlay" />
                    <BootRestoring key="status" active={false} />
                </>
            )
        case 'ready':
            return (
                <>
                    <Fragment key="app">{children}</Fragment>
                    {fading ? <BootBrandedLoading key="overlay" fadingOut /> : null}
                    <BootRestoring key="status" active={false} />
                </>
            )
        case 'expired':
            return (
                <>
                    <Fragment key="app">{children}</Fragment>
                    <Fragment key="expired">{expired}</Fragment>
                    <BootRestoring key="status" active={false} />
                </>
            )
        case 'failed':
            if (phase.error instanceof NoDomainsError) {
                return <BootNoDomains />
            }
            return (
                <BootFailed
                    tier={phase.tier}
                    kind={phase.kind}
                    referenceId={errorReferenceId(phase.error)}
                    reason={
                        phase.error instanceof AuthorizationRefusedError ? phase.error.providerDescription : undefined
                    }
                    onRetry={() => send({ type: 'retried' })}
                />
            )
        case 'unauthenticated':
            // The same keyed shape as ready: a logout keeps the app mounted instead of replacing it with a new copy.
            return (
                <>
                    <Fragment key="app">{unauthenticated ?? null}</Fragment>
                    <BootRestoring key="status" active={false} />
                </>
            )
    }
}
